'use client';
import Image from 'next/image';

export type SignalTierKey = 'seed' | 'seedling' | 'growing' | 'flowering' | 'established' | 'trusted';

export type SignalTier = {
  key: SignalTierKey;
  label: string;
  min: number;
  max: number; // inclusive-exclusive on the upper bound where relevant; Infinity for the top tier
  image: string;
  // "Natural" size used anywhere there's room (Settings, garden header, etc).
  // Seed tiny -> Trusted dramatically bigger, per the approved sizing decision.
  naturalSize: number;
  // Multiplier applied to the actual rendered image, on top of whatever
  // size (naturalSize or an explicit override) was requested. Exists
  // because some tiers' source art has noticeably less built-in padding
  // around the subject than the others, so at the same nominal box size
  // they read as visually bigger/denser rather than just "the next tier
  // up." This was previously patched as a one-off on the Info page only
  // (Seedling rendered at 30px vs everyone else's 44px there) -- that
  // patch didn't help anywhere else that renders SignalPlant, which is
  // exactly why the garden's hero plant (and anywhere else using a shared
  // explicit size) still showed it. 1 = no compensation needed.
  visualScale?: number;
};

// NOTE: filenames assumed as plant-seed / plant-seedling / plant-growing /
// plant-flowering / plant-established / plant-trusted, based on "six files,
// plant-seed.png through plant-trusted.png" in the handoff doc. Run
// `ls public/*.png` and tell me if the actual names differ -- easy one-line
// fix in the TIERS array below if so.
export const TIERS: SignalTier[] = [
  // Same 0.68 scale as Seedling, as a starting guess -- not confirmed the
  // same way Seedling's was (that one came from a direct side-by-side
  // comparison on the Info page). Seed and Seedling are close in size
  // (naturalSize 28 vs 36) and likely the same asset-generation batch, so
  // this is a reasonable first try. Adjust the number if it still looks
  // off once you actually see it.
  { key: 'seed',        label: 'Seed',        min: 0,   max: 0,        image: '/plant-seed.png',        naturalSize: 28,  visualScale: 0.68 },
  // 0.68 carries forward the exact ratio already established as correct on
  // the Info page (30/44) -- best available evidence, but worth eyeballing
  // on an actual phone since that fix was tuned for a different context
  // (fixed 30px/44px heights side by side) than a scale multiplier applied
  // on top of a dynamically-computed hero size.
  { key: 'seedling',    label: 'Seedling',    min: 0,   max: 24,       image: '/plant-seedling.png',    naturalSize: 36,  visualScale: 0.68 },
  { key: 'growing',     label: 'Growing',     min: 25,  max: 49,       image: '/plant-growing.png',     naturalSize: 48,  visualScale: 1 },
  { key: 'flowering',   label: 'Flowering',   min: 50,  max: 89,       image: '/plant-flowering.png',   naturalSize: 64,  visualScale: 1 },
  { key: 'established', label: 'Established', min: 90,  max: 149,     image: '/plant-established.png', naturalSize: 84,  visualScale: 1 },
  { key: 'trusted',     label: 'Trusted',     min: 150, max: Infinity, image: '/plant-trusted.png',     naturalSize: 112, visualScale: 1 },
];

// Happy-variant image per tier, shown on hover/pin instead of the neutral
// face. Seed has no entry -- it never had a face in the source art, so
// there's nothing to swap to; hovering it just keeps the neutral image.
const HAPPY_IMAGES: Partial<Record<SignalTierKey, string>> = {
  seedling: '/plant-seedling-happy.png',
  growing: '/plant-growing-happy.png',
  flowering: '/plant-flowering-happy.png',
  established: '/plant-established-happy.png',
  trusted: '/plant-trusted-happy.png',
};

// Central place both SignalPlant and the Garden page use to figure out
// which of the 6 tiers a person is in. ratings_count === 0 always wins,
// regardless of score, since that's the pre-stage.
export function getSignalTier(score: number, ratingsCount: number): SignalTier {
  if (!ratingsCount || ratingsCount <= 0) return TIERS[0];
  if (score < 25) return TIERS[1];
  if (score < 50) return TIERS[2];
  if (score < 90) return TIERS[3];
  if (score < 150) return TIERS[4];
  return TIERS[5];
}

export default function SignalPlant({
  score,
  ratingsCount,
  variant = 'natural',
  size,
  happy = false,
}: {
  score: number;
  ratingsCount: number;
  // 'badge' = fixed small size for the cramped nav bar.
  // 'natural' = tier-appropriate relative size (Settings, etc).
  variant?: 'badge' | 'natural';
  // Explicit override, in px. Use this where the display size shouldn't
  // depend on tier at all -- e.g. the garden's central plant.
  size?: number;
  // Shows the happy-variant image instead of the neutral one, where one
  // exists for this tier (Seed has none -- it never had a face).
  happy?: boolean;
}) {
  const tier = getSignalTier(score, ratingsCount);
  const resolvedSize = size ?? (variant === 'badge' ? 18 : tier.naturalSize);
  const usingHappy = happy && !!HAPPY_IMAGES[tier.key];
  const imageSrc = usingHappy ? HAPPY_IMAGES[tier.key]! : tier.image;
  const scale = tier.visualScale ?? 1;
  const renderedSize = resolvedSize * scale;

  return (
    // Outer box stays at the full requested size (naturalSize or an
    // explicit override) so anything positioning based on that size --
    // e.g. the garden page's absolute-positioned hero plant -- doesn't
    // shift. The actual image is scaled down inside it, bottom-anchored
    // and horizontally centered, same visual anchor point objectPosition
    // used to give it before scaling was introduced.
    <div style={{
      width: resolvedSize, height: resolvedSize, flexShrink: 0,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <Image
        src={imageSrc}
        alt={`${tier.label} signal plant`}
        width={renderedSize}
        height={renderedSize}
        style={{
          width: renderedSize, height: renderedSize, objectFit: 'contain', objectPosition: 'bottom', display: 'block',
          // The happy-variant source images rendered noticeably lighter than
          // the originals -- this is an approximate correction via CSS
          // rather than re-exporting the assets, easy to retune if it's not
          // quite right.
          filter: usingHappy ? 'saturate(1.25) brightness(0.93) contrast(1.05)' : undefined,
        }}
      />
    </div>
  );
}
